import { create } from 'zustand';
import type { Channel, EventEnvelope } from '@contracts/events';
import type { Candle, ProviderStatus, Quote, Timeframe } from '@contracts/market';
import type { SignalAlert, SignalOutcomeEvent, SignalStatus, SignalsSnapshot } from '@contracts/signals';
import type { ConnectionState } from '../lib/ws';

const MAX_ITEMS = 50;
const MAX_ALERTS = 100;

export interface SystemNotice {
  kind: string;
  severity: string;
  message: string;
  at: string;
}

export interface LiveState {
  connections: Record<Channel, ConnectionState>;
  reconnectAttempts: Record<Channel, number>;
  gapCount: number;
  resyncToken: number;
  provider: ProviderStatus | null;
  quote: Quote | null;
  quoteReceivedAt: number | null;
  tick: 'up' | 'down' | null;
  candles: Partial<Record<Timeframe, Candle>>;
  notices: SystemNotice[];
  signalStatus: SignalStatus | null;
  alerts: SignalAlert[];
  /** Alerts received live in this session and not yet dismissed (drives the in-app toast). */
  freshAlerts: SignalAlert[];
  dismissAlert: (id: string) => void;
  setConnection: (channel: Channel, state: ConnectionState, attempt: number) => void;
  registerGap: () => void;
  handleEvent: (channel: Channel, env: EventEnvelope) => void;
  reset: () => void;
}

const initial = {
  connections: {
    market: 'closed',
    system: 'closed',
    signals: 'closed',
    sr: 'closed',
  } as Record<Channel, ConnectionState>,
  reconnectAttempts: { market: 0, system: 0, signals: 0, sr: 0 } as Record<Channel, number>,
  gapCount: 0,
  resyncToken: 0,
  provider: null,
  quote: null,
  quoteReceivedAt: null,
  tick: null,
  candles: {},
  notices: [],
  signalStatus: null,
  alerts: [],
  freshAlerts: [],
};

function upsert(list: SignalAlert[], a: SignalAlert): SignalAlert[] {
  const rest = list.filter((x) => x.id !== a.id);
  return [a, ...rest].sort((x, y) => y.createdAt.localeCompare(x.createdAt)).slice(0, MAX_ALERTS);
}

export const useLive = create<LiveState>((set, get) => ({
  ...initial,
  setConnection: (channel, state, attempt) =>
    set((s) => ({
      connections: { ...s.connections, [channel]: state },
      reconnectAttempts: { ...s.reconnectAttempts, [channel]: attempt },
    })),
  registerGap: () => set((s) => ({ gapCount: s.gapCount + 1, resyncToken: s.resyncToken + 1 })),
  reset: () => set({ ...initial }),
  dismissAlert: (id) => set((s) => ({ freshAlerts: s.freshAlerts.filter((a) => a.id !== id) })),
  handleEvent: (_channel, env) => {
    const p = env.payload as Record<string, unknown>;
    switch (env.type) {
      case 'market.snapshot':
      case 'market.provider': {
        const status = p as unknown as ProviderStatus;
        const update: Partial<LiveState> = { provider: status };
        // A snapshot quote counts as received only if the backend says it is fresh.
        if (status.quote && !get().quote && status.quoteAgeMs !== null) {
          update.quote = status.quote;
          update.quoteReceivedAt = Date.now() - status.quoteAgeMs;
        }
        set(update);
        break;
      }
      case 'market.quote': {
        const q = p as unknown as Quote;
        const prev = get().quote;
        set({
          quote: q,
          quoteReceivedAt: Date.now(),
          tick: prev ? (q.mid > prev.mid ? 'up' : q.mid < prev.mid ? 'down' : get().tick) : null,
        });
        break;
      }
      case 'market.candle': {
        const c = p as unknown as Candle;
        set((s) => ({ candles: { ...s.candles, [c.timeframe]: c } }));
        break;
      }
      case 'system.status':
        set((s) => ({
          notices: [
            {
              kind: String(p.kind ?? 'system'),
              severity: String(p.severity ?? 'info'),
              message: String(p.message ?? ''),
              at: env.occurredAt,
            },
            ...s.notices,
          ].slice(0, MAX_ITEMS),
        }));
        break;
      case 'signals.snapshot': {
        const snap = p as unknown as SignalsSnapshot;
        set({
          signalStatus: snap.status,
          alerts: snap.alerts.slice(0, MAX_ALERTS),
        });
        break;
      }
      case 'signal.status':
        set({ signalStatus: p as unknown as SignalStatus });
        break;
      case 'signal.alert': {
        const a = p as unknown as SignalAlert;
        set((s) => ({
          alerts: upsert(s.alerts, a),
          freshAlerts: [a, ...s.freshAlerts].slice(0, 3),
        }));
        break;
      }
      case 'signal.outcome': {
        const { alert } = p as unknown as SignalOutcomeEvent;
        set((s) => ({
          alerts: upsert(s.alerts, alert),
          freshAlerts: s.freshAlerts.map((x) => (x.id === alert.id ? alert : x)),
        }));
        break;
      }
      default:
        break;
    }
  },
}));
